import './styles/index.css';
import {
  BRAND, DEFAULT_CONFIG, PENDING_DICTIONARY, nickname,
  type Action, type GameEvent, type MatchConfig, type WireState,
} from '@nt2/engine';
import { audio } from './audio/engine.js';
import { dictionaryFor, loadManifest, loadPack } from './dict/loader.js';
import type { PackMeta } from './dict/pack.js';
import { LocalTransport } from './net/local.js';
import { linkFor, newRoomId, newRoomKey, readLink, socketUrl, writeLink, type RoomLink } from './net/room.js';
import { WsTransport, type ConnectionStatus } from './net/transport.js';
import {
  applyDocumentPrefs, deviceSecret, prefs, setPrefs,
} from './state/prefs.js';
import { GameStore } from './state/store.js';
import { App } from './ui/app.js';
import { openMatchOptions, openPrivacy, openSettings, setRulesHooks } from './ui/dialogs.js';
import { openHowToPlay } from './ui/demo.js';
import { openContact } from './ui/contact.js';
import { clear, el } from './ui/dom.js';
import { Lobby } from './ui/lobby.js';
import { toast, toastHost } from './ui/toast.js';
import { confirmModal } from './ui/modal.js';
import { watchForUpdates, type UpdateWatcher } from './net/updates.js';
import { rememberedRules, rulesToRemember } from './state/rules.js';
import { runCountdown } from './ui/countdown.js';

document.title = BRAND.describe();
applyDocumentPrefs();
audio.configure({
  muted: prefs().muted, theme: prefs().soundTheme, volume: prefs().volume,
});

const mount = document.getElementById('app') as HTMLElement;
mount.removeAttribute('aria-busy');
// Mounted once for the session: a message refused in the lobby is just as
// worth showing as one refused mid-match.
toastHost(document.body);

const store = new GameStore(PENDING_DICTIONARY);
/**
 * This device's private identity. Online it is sent only to the server, which
 * turns it into the public player id by a one-way hash; it must never be shown
 * or put in a link. (Messages still carry it in their `playerId` field, but
 * the server discards that and uses the socket's identity instead.)
 */
const device = deviceSecret();

/**
 * Size the app to the height the browser really lays it out in.
 *
 * `100dvh` overstated it in the installed app on Android (see base.css), so
 * the true figure is measured — the smaller of the window's inner height and
 * the root element's client height, which agree everywhere that behaves — and
 * kept current as the window, the keyboard or the orientation change. Any
 * scroll of the document itself is undone: nothing lives outside the screen.
 */
function fitViewport(): void {
  const a = window.innerHeight, b = document.documentElement.clientHeight;
  const h = a > 0 && b > 0 ? Math.min(a, b) : a || b;
  if (h > 0) document.documentElement.style.setProperty('--app-h', `${h}px`);
  if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
}
fitViewport();
for (const ev of ['resize', 'orientationchange', 'pageshow']) window.addEventListener(ev, fitViewport);
window.visualViewport?.addEventListener('resize', fitViewport);
document.addEventListener('visibilitychange', fitViewport);
window.addEventListener('scroll', () => {
  if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
}, { passive: true });

let packs: PackMeta[] = [];
let dictReady = false;
let dictError: string | null = null;
let link: RoomLink | null = null;
/** True from making a table until the server first answers for it. */
let creating = false;
/** Set up at the end of the file; a newer deployed build is noticed here. */
let updates: UpdateWatcher | null = null;
/** The newer build's label while its notice is showing. */
let updateOffered: string | null = null;
let app: App | null = null;
let lobby: Lobby | null = null;
/**
 * The rules in force, and the rules this device last chose.
 *
 * A host who likes five letters and no word checking had to set that up again
 * every single time. The remembered patch is spread over the current defaults
 * rather than stored whole, so a build that adds a setting still starts from a
 * sane value for it instead of `undefined`.
 */
let lastConfig: MatchConfig = rememberedRules(prefs().lastRules);

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

/**
 * The default word list is fetched before anything can be started.
 *
 * A match must never begin with a dictionary that has not arrived: every
 * player would see their words marked unknown, and the take-two check would be
 * unanswerable. So the wait happens here, once, in the lobby.
 */
async function boot(): Promise<void> {
  /*
   * An invite link goes straight to its table.
   *
   * It used to open the front page and join only once the word list — four
   * megabytes — had finished downloading, so on a phone the link appeared to
   * do nothing, and somebody tapping around in the meantime could wander off
   * before it ever connected. Joining a waiting room needs no dictionary; the
   * list carries on loading alongside, and the match itself waits for it
   * (see `swapDictionary` in `onState`).
   */
  const invited = readLink();
  // Joined before the first paint, so that paint is "joining table …" and
  // never the front page.
  if (invited) joinRoom(invited);
  showLobby();
  try {
    packs = await loadManifest();
    await loadPack(DEFAULT_CONFIG.wordList);
    store.setDictionary(await dictionaryFor(lastConfig.wordList, lastConfig.spelling));
    dictReady = true;
  } catch (err) {
    dictError = err instanceof Error ? err.message : 'Could not load the word list.';
  }
  renderLobby();
  if (dictReady && !invited) maybeAutoStart();
}

/**
 * Development-only shortcut into a running match.
 *
 * On a real device there is no way to script taps, so reaching the board to
 * check how it sits against the browser's own furniture means tapping through
 * the lobby by hand every time. `?dev=solo` skips that.
 *
 * Guarded by `import.meta.env.DEV`, so it is dead code in any build that ships
 * — Vite strips the whole branch.
 */
function maybeAutoStart(): void {
  if (!import.meta.env.DEV) return;
  const params = new URLSearchParams(location.search);
  if (params.get('dev') !== 'solo') return;
  setPrefs({ name: prefs().name || 'Dev' });
  startSolo();
  showAudioProbe();
  // The engine needs the seat to exist before it can be dealt to.
  const tick = window.setInterval(() => {
    if (!store.wire) return;
    if (store.wire.phase !== 'lobby') { clearInterval(tick); return; }
    if (store.wire.seats.length > 0) store.send({ t: 'start', playerId: device } as Action);
  }, 120);
  window.setTimeout(() => clearInterval(tick), 5000);
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

/**
 * Which screen is up, and the history entry that goes with it.
 *
 * Android has a hardware back gesture and a phone browser has one in the
 * chrome, and until now both of them left the game — losing the table — with
 * no warning. Entering a table or a match pushes an entry, so back lands on
 * the screen before it; in a live match it asks first, because a stray edge
 * swipe must not end a game for four other people.
 */
type Screen = 'home' | 'room' | 'game';
let screen: Screen = 'home';

function setScreen(next: Screen): void {
  if (next === screen) return;
  const deeper = screen === 'home' && next !== 'home';
  screen = next;
  if (deeper) history.pushState({ nt2: next }, '');
}

window.addEventListener('popstate', () => {
  if (screen === 'game') {
    history.pushState({ nt2: 'game' }, '');
    app?.promptExit();
  } else if (screen === 'room') {
    goHome();
  }
});

function showLobby(): void {
  app?.destroy();
  app = null;
  lobby = new Lobby({
    onName: (name) => { if (store.wire) store.send({ t: 'rename', playerId: device, name }); },
    onTakeSeat: (seatId) => store.send({ t: 'takeSeat', playerId: device, seatId }),
    onLeaveSeat: () => store.send({ t: 'newSeat', playerId: device, name: prefs().name }),
    onStart: () => startMatch(),
    onOptions: (focus) => openOptions(focus),
    onSettings: () => openSettings(),
    onRules: () => openHowToPlay(store.wire?.config ?? lastConfig),
    onPrivacy: () => openPrivacy(store.wire?.config ?? lastConfig),
    onContact: () => openContact('Feedback'),
    onSoloStart: () => startSolo(),
    onCreateRoom: () => createRoom(),
    onJoinRoom: (code) => joinFromInput(code),
    onBack: () => goHome(),
    onStandardRules: () => useStandardRules(),
    onTour: () => openHowToPlay(store.wire?.config ?? lastConfig),
  });
  clear(mount);
  mount.append(lobby.root);
  renderLobby();
  if (updateOffered) placeUpdateBar();
}

function renderLobby(): void {
  if (!lobby) return;
  const wire = store.wire;
  if (wire && wire.phase === 'lobby' && wire.seats.length > 0) {
    setScreen('room');
    lobby.renderRoom(wire, link, store.isHost);
  } else if (link && creating && store.state.status !== 'rejected' && store.state.status !== 'closed') {
    // Our own new table, before the server has answered: draw it now.
    setScreen('room');
    lobby.renderRoom(null, link, true);
  } else if (link && store.state.status !== 'rejected' && store.state.status !== 'closed') {
    lobby.renderJoining(link.roomId);
  } else {
    setScreen('home');
    lobby.renderHome(dictReady, dictError);
  }
}


function showGame(): void {
  lobby = null;
  setScreen('game');
  app = new App(store, {
    onOpenMatchOptions: () => openOptions(),
    onLeave: () => goHome(),
  });
  clear(mount);
  mount.append(app.root);
  app.render();
  app.board.centreOnContent();
  if (updateOffered) placeUpdateBar();
}

/**
 * Back to the front door, without reloading the page.
 *
 * Making a table used to be a one-way door: there was no way back to the start
 * short of closing the tab, and leaving a match reloaded the whole page —
 * throwing away the loaded word list, four megabytes of it, and making a
 * two-second wait out of what should be instant. This closes the socket,
 * empties the state and shows the front door. The invite key is dropped from
 * the address bar too, or the next reload walks straight back into the table
 * that was just left.
 */
function goHome(): void {
  screen = 'home';
  creating = false;
  store.detach();
  store.applyState(emptyWire());
  link = null;
  writeLink(null);
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);
  // Back at the front door with nothing to lose: the moment to take an
  // update that arrived during a match.
  if (updates?.applyIfReady()) return;
  showLobby();
}

/** Throw away this table's variations and play the game as it comes. */
function useStandardRules(): void {
  const patch: MatchConfig = { ...DEFAULT_CONFIG, seed: lastConfig.seed };
  lastConfig = patch;
  setPrefs({ lastRules: {} });
  if (store.wire) store.send({ t: 'configure', playerId: device, config: patch } as Action);
  else renderLobby();
  void swapDictionary(patch.wordList, patch.spelling);
  toast('Back to the standard rules.', 'good');
}

const emptyWire = (): WireState => ({
  matchId: '', phase: 'lobby', config: lastConfig, seats: [], players: [],
  bagCount: 0, leftoverCount: 0, roundsTaken: 0, roundsLeft: 0,
  tilesToCome: 0, stuckCount: 0, stuckNeeded: 0, forcedDrawAt: null,
  startedAt: null, finishedAt: null, winnerSeatId: null, wentOutSeatId: null, log: [],
  version: 0, youSeatId: null, youPlayerId: null, series: { games: 0, totals: {} },
});

// ---------------------------------------------------------------------------
// Transport wiring
// ---------------------------------------------------------------------------

const handlers = {
  onState(wire: WireState): void {
    creating = false;
    const wasPlaying = store.wire?.phase === 'playing' || store.wire?.phase === 'finished';
    lastConfig = wire.config;
    store.applyState(wire);
    const nowPlaying = wire.phase === 'playing' || wire.phase === 'finished';
    if (nowPlaying && !wasPlaying) {
      /*
       * Loading a word list takes a moment, and in that moment the match can
       * have moved on — a player leaving a finished game and starting another
       * is enough. Showing the board because of a state that has since been
       * replaced puts a dead match on screen, so the phase is checked again
       * when the list actually arrives.
       */
      void swapDictionary(wire.config.wordList, wire.config.spelling).then(() => {
        const now = store.wire?.phase;
        if (now === 'playing' || now === 'finished') showGame();
        else renderLobby();
      });
    } else if (!nowPlaying && app) {
      showLobby();
    } else if (!nowPlaying) {
      renderLobby();
    }
  },
  onEvents(events: unknown[]): void {
    for (const ev of events as GameEvent[]) {
      // Shown over whatever screen is up: the board is still being built
      // behind it while the word list loads.
      if (ev.e === 'started' && ev.countdownMs > 0) runCountdown(ev.countdownMs);
    }
    app?.handleEvents(events as GameEvent[]);
  },
  onError(message: string): void {
    toast(message, 'bad', { ms: 3200 });
    store.setError(message);
  },
  onStatus(status: ConnectionStatus): void {
    store.setStatus(status);
    if (status === 'reconnecting') toast('Connection lost — trying again…', 'bad');
    else if (status === 'rejected') {
      toast('That table would not let us in. The link may be wrong, or the match has started.',
        'bad', { ms: 6000 });
      goHome();
    }
  },
};

/** Load whichever list the match actually settled on. */
async function swapDictionary(
  id: MatchConfig['wordList'], spelling: MatchConfig['spelling'] = 'both',
): Promise<void> {
  try {
    store.setDictionary(await dictionaryFor(id, spelling));
  } catch {
    toast('Could not load that word list — falling back to the default.', 'bad');
  }
}

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

function startSolo(): void {
  link = null;
  writeLink(null);
  store.attach(new LocalTransport(
    handlers, () => store.state.dict, device, playerName(),
    { ...lastConfig, seed: newRoomKey() }));
}

function createRoom(): void {
  link = { roomId: newRoomId(), key: newRoomKey() };
  writeLink(link);
  setPrefs({ lastRoom: linkFor(link) });
  creating = true;
  connect(link);
  renderLobby();
}

function joinRoom(r: RoomLink): void {
  creating = false;
  link = r;
  writeLink(r);
  connect(r);
}

function connect(r: RoomLink): void {
  store.attach(new WsTransport(socketUrl(r, device, playerName()), handlers));
}

/**
 * The name to sit down with.
 *
 * Somebody arriving by invite link never sees the name field, so they used to
 * turn up as "Player" — and a table of four Players is no use to anyone. They
 * get a made-up name like everybody else, remembered for next time.
 */
function playerName(): string {
  const saved = prefs().name.trim();
  if (saved) return saved;
  const made = nickname();
  setPrefs({ name: made });
  return made;
}

/** Accept a pasted full link, or a bare code if the key is already in the URL. */
function joinFromInput(raw: string): void {
  const text = raw.trim();
  const fromUrl = text.includes('#')
    ? readLink(text.slice(text.indexOf('#')))
    : null;
  if (fromUrl) { joinRoom(fromUrl); return; }
  toast(
    'That is not a complete invite. Paste the whole link — the code alone will not get you in.',
    'bad', { ms: 5200 });
}

function startMatch(): void {
  audio.unlock();
  store.send({ t: 'start', playerId: device } as Action);
}

function openOptions(focus?: string): void {
  const seatCount = store.wire?.seats.length ?? 1;
  openMatchOptions(store.wire?.config ?? lastConfig, seatCount, packs, (patch) => {
    if (store.wire) store.send({ t: 'configure', playerId: device, config: patch } as Action);
    lastConfig = { ...lastConfig, ...patch };
    // Remembered for next time, minus the seed: reusing a seed would deal the
    // same bag twice, which is the one thing a saved setting must not do.
    setPrefs({ lastRules: rulesToRemember(lastConfig) });
    if (patch.wordList || patch.spelling) void swapDictionary(lastConfig.wordList, lastConfig.spelling);
  }, { focus });
}

/*
 * The Rules tab in Settings edits the rules this device starts tables with.
 * They are the remembered rules, not the live table's: changing them in the
 * middle of a match changes nothing for the people in it.
 */
setRulesHooks({
  current: () => rememberedRules(prefs().lastRules),
  edit: (focus) => {
    openMatchOptions(rememberedRules(prefs().lastRules), 1, packs, (patch) => {
      const next = { ...rememberedRules(prefs().lastRules), ...patch };
      setPrefs({ lastRules: rulesToRemember(next) });
      if (!store.wire?.matchId) lastConfig = { ...next, seed: lastConfig.seed };
    }, { focus, title: 'Rules for new tables' });
  },
  reset: () => {
    setPrefs({ lastRules: {} });
    if (!store.wire?.matchId) lastConfig = { ...DEFAULT_CONFIG, seed: lastConfig.seed };
  },
});

/**
 * Development-only readout of the audio context state.
 *
 * Audio cannot be heard through a simulator screenshot, and "the code handles
 * the unlock" is not the same as "the unlock happened". This makes the one
 * fact that matters visible: whether the context reached `running`.
 */
function showAudioProbe(): void {
  if (!import.meta.env.DEV) return;
  const badge = el('div', {
    style: 'position:fixed;left:8px;bottom:calc(96px + env(safe-area-inset-bottom));' +
      'z-index:999;font:600 11px/1.3 ui-monospace,monospace;padding:3px 7px;' +
      'border-radius:6px;background:#000c;color:#ffcc48;pointer-events:none;' +
      'border:1px solid #ffcc4855',
  });
  document.body.append(badge);
  const paint = (): void => {
    const a = audio.settings;
    badge.textContent =
      `audio ${audio.contextState}${a.muted ? ' muted' : ''} ${a.theme}`;
  };
  paint();
  setInterval(paint, 400);
}

// ---------------------------------------------------------------------------

window.addEventListener('hashchange', () => {
  const r = readLink();
  if (r && (!link || r.roomId !== link.roomId)) joinRoom(r);
});

// Keep the screen awake during a match where the device allows it: players go
// minutes without touching the screen while thinking, and a lock mid-race is
// the most annoying possible interruption.
let wakeLock: { release(): Promise<void> } | null = null;
async function keepAwake(on: boolean): Promise<void> {
  try {
    const wl = (navigator as unknown as {
      wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
    }).wakeLock;
    if (!wl) return;
    if (on && !wakeLock) wakeLock = await wl.request('screen');
    else if (!on && wakeLock) { await wakeLock.release(); wakeLock = null; }
  } catch { /* denied or unsupported */ }
}
store.subscribe(() => { void keepAwake(store.wire?.phase === 'playing'); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && store.wire?.phase === 'playing') {
    void keepAwake(true);
  }
});

/*
 * Tapping away from a text field puts the keyboard away.
 *
 * On a phone the keyboard covers half the screen and there is no obvious way
 * to dismiss it otherwise — the buttons underneath simply cannot be reached.
 */
document.addEventListener('pointerdown', (e) => {
  const active = document.activeElement;
  if (!(active instanceof HTMLInputElement) && !(active instanceof HTMLTextAreaElement)) return;
  if (e.target instanceof Node && active.contains(e.target)) return;
  if (e.target === active) return;
  active.blur();
}, true);

// A last-resort surface for anything that escapes: silence is worse than an
// apology, and a player should never be left staring at a frozen board.
window.addEventListener('error', (e) => {
  toast(`Something went wrong: ${e.message}`, 'bad', { ms: 6000 });
});
window.addEventListener('unhandledrejection', () => {
  toast('Something went wrong in the background.', 'bad');
});

/**
 * A newer build, noticed while the app was open.
 *
 * On the front page it simply reloads. Anywhere else a reload could cost
 * something — a solo match lives only in this page — so it says so and offers
 * the button, and otherwise waits for the way back to the front page.
 *
 * The notice never floats over anything that can be pressed: in a match it
 * sits in the strip between the board and the rack, where the tips go, and on
 * the other screens at the foot of the page. It is placed again whenever the
 * screen changes.
 */
function offerUpdate(label: string): void {
  updateOffered = label;
  placeUpdateBar();
}

function placeUpdateBar(): void {
  document.querySelector('.updatebar')?.remove();
  if (!updateOffered) return;
  const now = el('button', { class: 'btn btn--sm btn--primary', type: 'button', text: 'Update now' });
  const later = el('button', {
    class: 'btn btn--sm btn--ghost', type: 'button', 'aria-label': 'Later', text: '✕',
  });
  const bar = el('div', { class: 'updatebar', role: 'status' },
    el('span', { text: `Puletiwo v${updateOffered} is ready. It loads when you go back to the menu, or now:` }),
    now, later);
  now.addEventListener('click', async () => {
    const soloGame = screen === 'game' && !link;
    if (soloGame && !await confirmModal('Update now?',
      'This solo match only lives on this screen, so updating ends it.', 'Update')) return;
    updates?.applyIfReady(true);
  });
  later.addEventListener('click', () => { updateOffered = null; bar.remove(); });
  const rack = document.querySelector('.shell > .rack');
  if (rack) {
    bar.classList.add('updatebar--inline');
    rack.before(bar);
  } else {
    document.body.append(bar);
  }
}

updates = watchForUpdates({
  safeToReload: () => screen === 'home' && !link && !creating,
  onReady: offerUpdate,
});

void boot();

export { el };
